/**
 * syncQueueStore — Module-level singleton for the offline sync queue.
 *
 * Pure JS (no React) so it can be imported from hooks and services without
 * creating circular dependencies.
 *
 * Operations shape:
 *   { id, type: 'save'|'delete', entityType: 'task'|'event', entityId, data?, queuedAt }
 */

const QUEUE_KEY = 'adhd-planner-sync-queue';

// ─── In-memory state (initialised from localStorage) ─────────────────────────

let _queue = [];
try {
  const raw = localStorage.getItem(QUEUE_KEY);
  _queue = raw ? JSON.parse(raw) : [];
} catch {
  _queue = [];
}

const _listeners = new Set();

function _persist() {
  localStorage.setItem(QUEUE_KEY, JSON.stringify(_queue));
  _listeners.forEach(fn => fn([..._queue]));
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Add an operation to the queue.
 * If an op for the same (entityType + entityId + type) already exists, it is
 * replaced so the queue never contains stale duplicates for the same entity.
 *
 * @param {{ type, entityType, entityId, data? }} op
 */
export function enqueue(op) {
  const operation = {
    id: `op-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    queuedAt: new Date().toISOString(),
    ...op,
  };

  // Deduplicate: replace existing op for same entity + type
  _queue = _queue.filter(
    o => !(o.type === op.type && o.entityType === op.entityType && o.entityId === op.entityId),
  );
  _queue = [..._queue, operation].sort((a, b) =>
    a.queuedAt.localeCompare(b.queuedAt),
  );
  _persist();
}

/** Remove a single operation by its id (after it was processed). */
export function dequeue(opId) {
  _queue = _queue.filter(o => o.id !== opId);
  _persist();
}

export function getQueue() {
  return [..._queue];
}

export function getPendingCount() {
  return _queue.length;
}

export function clearQueue() {
  _queue = [];
  localStorage.removeItem(QUEUE_KEY);
  _listeners.forEach(fn => fn([]));
}

/** Subscribe to queue changes. Returns an unsubscribe function. */
export function subscribeToQueue(fn) {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

// ─── Retry with exponential backoff ─────────────────────────────────────────

let _retryTimer = null;
let _retryCount = 0;
const MAX_RETRIES = 5;
const BASE_DELAY = 2000; // 2s

/**
 * Process the queue with a given handler function.
 * Retries failed operations with exponential backoff (2s, 4s, 8s, 16s, 32s).
 *
 * @param {(op) => Promise<boolean>} handler — async function that processes one op.
 *   Return true if successful (op will be dequeued), false to retry.
 */
export async function processQueue(handler) {
  if (_queue.length === 0) return { processed: 0, failed: 0 };

  let processed = 0;
  let failed = 0;

  for (const op of [..._queue]) {
    try {
      const success = await handler(op);
      if (success) {
        dequeue(op.id);
        processed++;
        _retryCount = 0; // Reset on success
      } else {
        failed++;
      }
    } catch (err) {
      console.warn(`Sync queue: op ${op.id} failed:`, err.message);
      failed++;
    }
  }

  // Schedule retry if there are failures
  if (failed > 0 && _retryCount < MAX_RETRIES) {
    const delay = BASE_DELAY * Math.pow(2, _retryCount);
    _retryCount++;
    console.log(`Sync queue: ${failed} failed, retrying in ${delay / 1000}s (attempt ${_retryCount}/${MAX_RETRIES})`);

    clearTimeout(_retryTimer);
    _retryTimer = setTimeout(() => processQueue(handler), delay);
  } else if (_retryCount >= MAX_RETRIES) {
    console.warn(`Sync queue: max retries reached (${MAX_RETRIES}), stopping`);
    _retryCount = 0;
  }

  return { processed, failed };
}

/** Stop any pending retry timer. */
export function cancelRetry() {
  clearTimeout(_retryTimer);
  _retryTimer = null;
  _retryCount = 0;
}
